/**
 * Storage utilities for localStorage access
 * Provides abstraction over localStorage for better testability and SSR safety
 *
 * Performance: Uses in-memory Map cache to avoid expensive synchronous
 * localStorage/sessionStorage reads on every access.
 */

import type { StoragePort } from "@/shared/lib/ports/storage";

// =============================================================================
// In-Memory Cache Layer
// =============================================================================

/**
 * In-memory cache for localStorage values.
 * Prevents repeated synchronous reads which block the main thread.
 *
 * Cache invalidation strategy:
 * - setItem: Updates cache immediately
 * - removeItem: Removes from cache
 * - clear: Clears entire cache
 * - External changes: Use invalidateCache() or listen to 'storage' event
 */
const localStorageCache = new Map<string, string | null>();

/**
 * In-memory cache for sessionStorage values.
 */
const sessionStorageCache = new Map<string, string | null>();

/**
 * Invalidate a specific key in the localStorage cache.
 * Call this when you know the value may have changed externally.
 */
export function invalidateLocalStorageCache(key: string): void {
	localStorageCache.delete(key);
}

/**
 * Invalidate a specific key in the sessionStorage cache.
 */
export function invalidateSessionStorageCache(key: string): void {
	sessionStorageCache.delete(key);
}

/**
 * Clear the entire localStorage cache.
 * Useful when switching users or on logout.
 */
export function clearLocalStorageCacheAll(): void {
	localStorageCache.clear();
}

/**
 * Clear the entire sessionStorage cache.
 */
export function clearSessionStorageCacheAll(): void {
	sessionStorageCache.clear();
}

// =============================================================================
// Cached Storage Adapters
// =============================================================================

/**
 * Default localStorage implementation with in-memory caching.
 * Reads are cached to avoid repeated synchronous localStorage access.
 */
export const localStorageAdapter: StoragePort = {
	getItem: (key: string): string | null => {
		if (typeof window === "undefined") {
			return null;
		}

		// Check cache first
		if (localStorageCache.has(key)) {
			return localStorageCache.get(key) ?? null;
		}

		// Cache miss - read from storage and cache result
		try {
			const value = window.localStorage.getItem(key);
			localStorageCache.set(key, value);
			return value;
		} catch {
			localStorageCache.set(key, null);
			return null;
		}
	},
	setItem: (key: string, value: string): void => {
		if (typeof window === "undefined") {
			return;
		}
		try {
			window.localStorage.setItem(key, value);
			// Update cache on write
			localStorageCache.set(key, value);
		} catch {
			// Silently fail if storage is unavailable
		}
	},
	removeItem: (key: string): void => {
		if (typeof window === "undefined") {
			return;
		}
		try {
			window.localStorage.removeItem(key);
			// Remove from cache
			localStorageCache.delete(key);
		} catch {
			// Silently fail if storage is unavailable
		}
	},
	clear: (): void => {
		if (typeof window === "undefined") {
			return;
		}
		try {
			window.localStorage.clear();
			// Clear entire cache
			localStorageCache.clear();
		} catch {
			// Silently fail if storage is unavailable
		}
	},
};

/**
 * SessionStorage implementation with in-memory caching.
 * Reads are cached to avoid repeated synchronous sessionStorage access.
 */
export const sessionStorageAdapter: StoragePort = {
	getItem: (key: string): string | null => {
		if (typeof window === "undefined") {
			return null;
		}

		// Check cache first
		if (sessionStorageCache.has(key)) {
			return sessionStorageCache.get(key) ?? null;
		}

		// Cache miss - read from storage and cache result
		try {
			const value = window.sessionStorage.getItem(key);
			sessionStorageCache.set(key, value);
			return value;
		} catch {
			sessionStorageCache.set(key, null);
			return null;
		}
	},
	setItem: (key: string, value: string): void => {
		if (typeof window === "undefined") {
			return;
		}
		try {
			window.sessionStorage.setItem(key, value);
			// Update cache on write
			sessionStorageCache.set(key, value);
		} catch {
			// Silently fail if storage is unavailable
		}
	},
	removeItem: (key: string): void => {
		if (typeof window === "undefined") {
			return;
		}
		try {
			window.sessionStorage.removeItem(key);
			// Remove from cache
			sessionStorageCache.delete(key);
		} catch {
			// Silently fail if storage is unavailable
		}
	},
	clear: (): void => {
		if (typeof window === "undefined") {
			return;
		}
		try {
			window.sessionStorage.clear();
			// Clear entire cache
			sessionStorageCache.clear();
		} catch {
			// Silently fail if storage is unavailable
		}
	},
};
